import React, { useEffect, useMemo, useRef, useState } from 'react'
import { VerifyEmailOtpPresenter } from './verify-email-opt-presenter'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../store'
import { userServices } from '../../services'
import { Verification } from '../../lib/types'
import { showBanner } from '../../global-state/banner-slice'
import { captureEvent } from '../../lib/utils/posthogUtils/usePostHog'
import { EVENTS } from '../../lib/utils/constants'
import { ContactUsModal } from '../../lib/components'
import {
  completeEmailVerificationLogin,
  getVerifyEmailError,
  isEmailVerificationLoginData,
} from '../../lib/utils/post-verification-auth'
import { applyPostLoginNavigation } from '../../lib/utils/post-login'
import './verify-email-opt.scss'

const OTP_LENGTH = 6
const RESEND_COUNTDOWN_SECONDS = 60

type VerifyAccountRouteState = {
  email?: string
  investorId?: number
}

const VerifyEmailOtpContainer = () => {
  const { email, userId } = useSelector((state: RootState) => state.investor)
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const [countdown, setCountdown] = useState(RESEND_COUNTDOWN_SECONDS)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [showContactUsModal, setShowContactUsModal] = useState(false)

  const routeState = location.state as VerifyAccountRouteState | null
  const resolvedEmail = useMemo(() => routeState?.email || email || '', [routeState?.email, email])
  const resolvedInvestorId = useMemo(
    () => routeState?.investorId || userId || 0,
    [routeState?.investorId, userId],
  )

  useEffect(() => {
    if (!resolvedEmail || !resolvedInvestorId) {
      navigate('/login')
    }
  }, [resolvedEmail, resolvedInvestorId, navigate])

  useEffect(() => {
    if (countdown === 0) return
    const timer = window.setTimeout(() => {
      setCountdown((previousValue) => Math.max(previousValue - 1, 0))
    }, 1000)
    return () => window.clearTimeout(timer)
  }, [countdown])

  const focusInput = (index: number) => {
    inputRefs.current[index]?.focus()
  }

  const handleOtpChange = (index: number, value: string) => {
    const parsedValue = value.replace(/\D/g, '').slice(-1)
    const updatedDigits = [...otpDigits]
    updatedDigits[index] = parsedValue
    setOtpDigits(updatedDigits)

    if (parsedValue && index < OTP_LENGTH - 1) {
      focusInput(index + 1)
    }
  }

  const handleOtpKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
      focusInput(index - 1)
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      focusInput(index - 1)
    }

    if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      focusInput(index + 1)
    }
  }

  const handleOtpPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault()
    const pastedDigits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pastedDigits) return

    const updatedDigits = Array(OTP_LENGTH)
      .fill('')
      .map((_, index) => pastedDigits[index] || '')
    setOtpDigits(updatedDigits)

    const focusIndex = Math.min(pastedDigits.length, OTP_LENGTH - 1)
    focusInput(focusIndex)
  }

  const handleVerify = async () => {
    const otp = otpDigits.join('')
    if (otp.length !== OTP_LENGTH) {
      dispatch(showBanner({ text: 'Please enter the 6-digit OTP.', variant: 'error-banner' }))
      return
    }

    setIsSubmitting(true)
    const response = await userServices.verifyEmail_v1(
      otp,
      String(resolvedInvestorId),
      Verification.verifyEmail,
    )
    setIsSubmitting(false)

    if (response?.status === 200 && isEmailVerificationLoginData(response.data)) {
      captureEvent(EVENTS.email_verified, { user: resolvedInvestorId })
      dispatch(
        showBanner({
          text: 'Email verified successfully.',
          variant: 'success-banner',
        }),
      )
      completeEmailVerificationLogin(dispatch, response.data)
      await applyPostLoginNavigation(
        dispatch,
        navigate,
        response.data.authToken,
        response.data.userId,
        response.data.email,
        response.data.name,
        response.data.acceptedNDA ?? false,
      )
      return
    }

    dispatch(
      showBanner({
        text:
          getVerifyEmailError(response?.data)?.message ||
          'Invalid or expired OTP. Please try again.',
        variant: 'error-banner',
      }),
    )
  }

  const handleResend = async () => {
    if (countdown > 0) return
    setIsResending(true)
    const response = await userServices.sendVerificationLink_v1(
      resolvedEmail,
      Verification.verifyEmail,
    )
    setIsResending(false)

    if (response?.status === 200) {
      captureEvent(EVENTS.email_verification_link_sent, { email: resolvedEmail })
      dispatch(
        showBanner({
          text: 'A new OTP has been sent to your email.',
          variant: 'success-banner',
        }),
      )
      setOtpDigits(Array(OTP_LENGTH).fill(''))
      setCountdown(RESEND_COUNTDOWN_SECONDS)
      focusInput(0)
      return
    }

    dispatch(
      showBanner({
        text: response?.data?.message || 'Something went wrong! Please try again.',
        variant: 'error-banner',
      }),
    )
  }

  return (
    <>
      <VerifyEmailOtpPresenter
        email={resolvedEmail}
        otpDigits={otpDigits}
        countdown={countdown}
        isSubmitting={isSubmitting}
        isResending={isResending}
        onOtpChange={handleOtpChange}
        onOtpKeyDown={handleOtpKeyDown}
        onOtpPaste={handleOtpPaste}
        onVerify={handleVerify}
        onResend={handleResend}
        setInputRef={(index, element) => {
          inputRefs.current[index] = element
        }}
        onOpenContactSupport={() => setShowContactUsModal(true)}
      />
      {showContactUsModal && (
        <ContactUsModal
          active={showContactUsModal}
          handleOnClose={() => setShowContactUsModal(false)}
          initialSubject='Account Management'
          initialEmail={resolvedEmail}
        />
      )}
    </>
  )
}

export { VerifyEmailOtpContainer }
